import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import * as _ from 'lodash';
import { Observable, Subject, of } from 'rxjs';
import { debounceTime, delay, startWith, takeUntil, tap } from 'rxjs/operators';

import { FastenerBasePointHelper } from '../../../helpers/fastener-base-point.helper';
import { CountertopFastenersSet, CountertopHole } from '../../../models/cutting/countertop/outline';
import {
  CustomHoleSchemaTypeEnum,
  CuttingCustomHoleSchema,
} from '../../../models/cutting/cutting-custom-hole-schema.model';
import { FastenerBasePointEnum } from '../../../models/cutting/cutting-custom-hole.model';
import { CuttingFastenersSet, PositioningTypeEnum } from '../../../models/cutting/cutting-fasteners-set.model';
import { AnglePosition, PlateTypeEnum } from '../../../models/cutting/cutting-panel-outline.model';
import { Position } from '../../../models/cutting/cutting-svg.model';
import { Countertop } from '../../../models/cutting/public-api';
import { CountertopSchemaComponent } from '../../countertop-schema/countertop-schema.component';
import { ApiLinks } from '../../cutting-panel-list/csl.api-links';
import { FastenerSessionStorageManager } from '../fastener-selection/fastener-session-storage-manager.helper';
import { EdgeHoleFormComponent } from './edge-hole-form/edge-hole-form.component';
import { PlateHoleFormComponent } from './plate-hole-form/plate-hole-form.component';

export interface CustomHoleParamsForm {
  type: PlateTypeEnum;
  coords: CustomHoleCoordinates[];
  depth: number;
}

export interface CustomHoleCoordinates {
  x: number;
  y: number;
  z: number;
  point: FastenerBasePointEnum;
  multiSide: Position;
}

@Component({
  selector: 'csl-countertop-fastener-base-point-select',
  templateUrl: './countertop-fastener-base-point-select.component.html',
  styleUrls: ['./countertop-fastener-base-point-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CountertopFastenerBasePointSelectComponent implements OnInit, AfterViewInit, OnDestroy {
  thisCustomHoleSchemaTypeEnum = CustomHoleSchemaTypeEnum;
  thisCustomHoleTypeEnum = PlateTypeEnum;
  thisPositioningTypeEnum = PositioningTypeEnum;
  data: CustomHoleParamsForm = { coords: [], depth: 0, type: FastenerSessionStorageManager.lastSelectedPlate };
  holeFormValid = false;
  _tempCountertop: Countertop;
  _originalCountertop: Countertop;
  _side: Position | undefined;
  viewBeenChecked = false;
  unsubscribe$: Subject<void> = new Subject();
  selectedMultiSide: Position = 'lc';
  selectedPoint: FastenerBasePointEnum = FastenerBasePointEnum.Start;
  hasMirroredHoles = false;
  mirrorAxis: 'x' | 'y' | 'xy' = 'y';
  action$: Observable<CustomHoleParamsForm>;

  @Input() defaultMirrorAxis: 'x' | 'y' | 'xy' = 'y';

  @Input() set countertop(val: Countertop) {
    this._originalCountertop = val;
    this._tempCountertop = _.cloneDeep(val);
  }
  get countertop(): Countertop {
    return this._tempCountertop;
  }
  @Input() side: Position | undefined;
  @Input() fastener: CuttingCustomHoleSchema | CuttingFastenersSet;
  @Input() mainMaterialThickness = 0;
  @Input() availablePlate: PlateTypeEnum;

  @Output() formChanged: EventEmitter<CustomHoleParamsForm> = new EventEmitter();

  @ViewChild('edgeSchema', { static: true }) edgeSchema: CountertopSchemaComponent;

  // @ViewChild('svgWorkspace', { static: true }) svgWorkspace: SvgWorkspaceComponent;
  @ViewChild('holeForm', { static: false }) holeForm: PlateHoleFormComponent | EdgeHoleFormComponent;

  get selectAnyBasePoint(): boolean {
    return this.side === undefined;
  }

  get fullMaterialThickness(): number {
    if (this.mainMaterialThickness !== 0) {
      return (
        this.mainMaterialThickness +
        this.countertop.outline.faceBanding.map(b => b.material.thickness).reduce((a, v) => a + v, 0)
      );
    } else {
      return 200;
    }
  }

  get fastenersSetIconLink(): string {
    return ApiLinks.fastenersSetIcon(this.fastener.id);
  }

  @HostBinding('class.fastener-base-point-select')
  fastenerBasePointSelect = true;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.mirrorAxis = this.defaultMirrorAxis;

    this.data.type = _.isNil(this.availablePlate) ? this.data.type : this.availablePlate;
  }

  ngAfterViewInit(): void {
    // this.edgeSchema.render(this.countertop);
    this.action$ = this.formChanged.pipe(
      debounceTime(300),
      startWith(null),
      tap(() => {
        this.edgeSchema.render(this.countertop);
      })
    );

    // time to set point and side to coordinated form
    of(0)
      .pipe(delay(1000), takeUntil(this.unsubscribe$))
      .subscribe(() => this.holeForm.addCoord());
  }

  isFastenersSet(item: CuttingCustomHoleSchema | CuttingFastenersSet): item is CuttingFastenersSet {
    return item instanceof CuttingFastenersSet;
  }

  mirrorFastener(
    fastener: CountertopFastenersSet | CountertopHole,
    axis: 'x' | 'y'
  ): CountertopFastenersSet | CountertopHole {
    const mirrored: CountertopFastenersSet | CountertopHole = _.cloneDeep(fastener);
    mirrored.basePoint =
      mirrored.basePoint === FastenerBasePointEnum.End ? FastenerBasePointEnum.Start : FastenerBasePointEnum.End;

    if (axis === 'x') {
      switch (fastener.position) {
        case 'tc':
          mirrored.position = 'bc';
          break;
        case 'bc':
          mirrored.position = 'tc';
          break;
      }
    } else if (axis === 'y') {
      switch (fastener.position) {
        case 'rc':
          mirrored.position = 'lc';
          break;
        case 'lc':
          mirrored.position = 'rc';
          break;
      }
    }

    return mirrored;
  }

  setBasePoint(point: AnglePosition): void {
    let setRes;

    if (this.fastener instanceof CuttingCustomHoleSchema) {
      setRes = FastenerBasePointHelper.setBasePoint(point, this.side);
    }

    if (this.fastener instanceof CuttingFastenersSet) {
      setRes = FastenerBasePointHelper.setBasePoint(
        point,
        this.side,
        this.fastener.type === PositioningTypeEnum.FreeOnSide
      );
    }

    if (!_.isUndefined(setRes.selectedPoint)) {
      this.selectedPoint = setRes.selectedPoint;
    }

    if (!_.isUndefined(setRes.selectedSide)) {
      this.selectedMultiSide = setRes.selectedSide;
    }

    if (/*this.data.point !== undefined && */ this.holeFormValid) {
      this.fillTempPanel(this.data, this.fastener, this.side);
      // this.svgWorkspace.draw(this.countertop);
      this.formChanged.emit(this.data);
    } else {
      this.formChanged.emit(undefined);
    }
  }

  holeFormChanged(holeFormData: CustomHoleParamsForm): void {
    if (holeFormData === undefined) {
      this.holeFormValid = false;

      this.formChanged.emit(undefined);
    } else {
      this.data.depth = holeFormData.depth;
      this.data.coords = holeFormData.coords;
      this.data.type = holeFormData.type;

      this.holeFormValid = true;

      this.fillTempPanel(this.data, this.fastener, this.side);
      // this.svgWorkspace.draw(this.countertop);

      if (this.hasMirroredHoles) {
        const data = _.cloneDeep(this.data);
        const mirrored = _.cloneDeep(this.data.coords);

        mirrored.forEach(h => {
          const getOppositeSide = (side: Position): Position => {
            return side === 'tc' ? 'bc' : side === 'bc' ? 'tc' : side === 'lc' ? 'rc' : 'lc';
          };

          // add holes for later moving them to real positions
          if (this.mirrorAxis === 'xy') {
            mirrored.push(_.cloneDeep(h));
            const last = (): CustomHoleCoordinates => mirrored[mirrored.length - 1];

            last().point =
              last().point === FastenerBasePointEnum.Start ? FastenerBasePointEnum.End : FastenerBasePointEnum.Start;

            mirrored.push(_.cloneDeep(h));

            last().multiSide = _.isNil(last().multiSide) ? last().multiSide : getOppositeSide(last().multiSide);
          }

          h.point = h.point === FastenerBasePointEnum.Start ? FastenerBasePointEnum.End : FastenerBasePointEnum.Start;

          if (
            (this.mirrorAxis === 'x' && (h.multiSide === 'tc' || h.multiSide === 'bc')) ||
            (this.mirrorAxis === 'y' && (h.multiSide === 'lc' || h.multiSide === 'rc')) ||
            this.mirrorAxis === 'xy'
          ) {
            h.multiSide = _.isNil(h.multiSide) ? h.multiSide : getOppositeSide(h.multiSide);
          }
        });

        data.coords = [...data.coords, ...mirrored];

        this.formChanged.emit(data);
      } else {
        this.formChanged.emit(this.data);
      }
    }
  }

  fillTempPanel(
    data: CustomHoleParamsForm,
    fastener: CuttingCustomHoleSchema | CuttingFastenersSet,
    side: Position
  ): void {
    if (fastener instanceof CuttingCustomHoleSchema) {
      this.setCustomHolesToTempPanel(data, fastener, side);
    }

    if (fastener instanceof CuttingFastenersSet) {
      this.setFastenerSetsToTempPanel(data, fastener, side);
    }
  }

  private setFastenerSetsToTempPanel(
    data: CustomHoleParamsForm,
    fastenersSet: CuttingFastenersSet,
    side: Position
  ): void {
    // const newFixedFastenerSetsTc: CountertopFastenersSet[] = [];
    // const newFixedFastenerSetsBc: CountertopFastenersSet[] = [];
    // const newFixedFastenerSetsRc: CountertopFastenersSet[] = [];
    // const newFixedFastenerSetsLc: CountertopFastenersSet[] = [];
    const newFixedFastenerSets: CountertopFastenersSet[] = [];

    // const newMirroredFastenerSetsTc: CountertopFastenersSet[] = [];
    // const newMirroredFastenerSetsBc: CountertopFastenersSet[] = [];
    // const newMirroredFastenerSetsRc: CountertopFastenersSet[] = [];
    // const newMirroredFastenerSetsLc: CountertopFastenersSet[] = [];
    const newMirroredFastenerSets: CountertopFastenersSet[] = [];

    data.coords.forEach(coord => {
      const newFixedFastenersSet: CountertopFastenersSet = {
        ...fastenersSet,
        basePoint: coord.point,
        plate: data.type,
        x: coord.x,
        y: coord.y,
        position: coord.multiSide || side,
      } as CountertopFastenersSet;

      newFixedFastenerSets.push(newFixedFastenersSet);

      if (this.hasMirroredHoles) {
        switch (this.mirrorAxis) {
          case 'xy':
            newMirroredFastenerSets.push(this.mirrorFastener(newFixedFastenersSet, 'x') as CountertopFastenersSet);
            newMirroredFastenerSets.push(
              this.mirrorFastener(_.last(newMirroredFastenerSets), 'y') as CountertopFastenersSet
            );
            newMirroredFastenerSets.push(this.mirrorFastener(newFixedFastenersSet, 'y') as CountertopFastenersSet);
            break;
          case 'x':
          case 'y':
            newMirroredFastenerSets.push(
              this.mirrorFastener(newFixedFastenersSet, this.mirrorAxis) as CountertopFastenersSet
            );
            break;
        }
      }
    });

    this._tempCountertop.outline.fastenersSets = [
      ..._.cloneDeep(this._originalCountertop.outline.fastenersSets),
      ...newFixedFastenerSets,
      ...newMirroredFastenerSets,
    ];
  }

  private setCustomHolesToTempPanel(data: CustomHoleParamsForm, schema: CuttingCustomHoleSchema, side: Position): void {
    const newHoles: CountertopHole[] = [];

    const newMirroredHoles: CountertopHole[] = [];

    data.coords.forEach(coord => {
      const newHole: CountertopHole = {
        schemaId: schema.id,
        name: schema.name,
        diameter: schema.diameter,
        basePoint: coord.point,
        x: coord.x,
        y: coord.y,
        z: coord.z,
        depth: data.depth,
        type: data.type,
        // if multiside, side = undefined
        position: side ?? coord.multiSide,
      };

      newHoles.push(newHole);

      if (this.hasMirroredHoles) {
        switch (this.mirrorAxis) {
          case 'xy':
            newMirroredHoles.push(this.mirrorFastener(newHole, 'x') as CountertopHole);
            newMirroredHoles.push(this.mirrorFastener(_.last(newMirroredHoles), 'y') as CountertopHole);
            newMirroredHoles.push(this.mirrorFastener(newHole, 'y') as CountertopHole);
            break;

          case 'x':
          case 'y':
            newMirroredHoles.push(this.mirrorFastener(newHole, this.mirrorAxis) as CountertopHole);
            break;
        }
      }
    });

    this._tempCountertop.outline.customHoles = [
      ..._.cloneDeep(this._originalCountertop.outline.customHoles),
      ...newHoles,
      ...newMirroredHoles,
    ];
  }

  hasImage(): boolean {
    return !_.isEmpty((this.fastener as CuttingFastenersSet).imageLink);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
